import classNames from 'classnames';
import PropTypes, {
  bool,
  number,
  oneOfType,
  shape,
  string,
  array,
} from 'prop-types';
import React from 'react';
import A from '../Anchor';
import RevisedAndPublished from '../RevisedAndPublished';
import { getRegionalizedUrl } from './external-search-utils';
import TrackedLink from './tracked-link';

const ListItem = ({
  className,
  href,
  image,
  vignette,
  heading,
  text,
  score,
  revisedAsPublished,
  isNew,
  eventDate,
  subjectHeading,
  translations,
  showRevised,
  TrackId,
  query,
  HitId,
  DisableClickTracking,
  informationtype,
  vardpersonal1177,
  selectionCode,
  specializedCareUrl,
  primaryCareUrl,
  isExternalSearchHit,
  regionalizeSearchHits,
  availableLanguagesHeader,
  availableLanguages,
}) => {
  function trackClick() {
    fetch(
      `/api/v2/search/trackclick?hitId=${HitId}&query=${query}&trackId=${TrackId}`
    );
  }
  const itemContainer = classNames({
    'c-related__item__container': true,
    'c-related__empty-container': text?.length === 0,
  });

  const trackLinkClick = () =>
    !DisableClickTracking && TrackId ? trackClick() : undefined;
  const isVardPersonal1177 =
    (vardpersonal1177 && selectionCode && primaryCareUrl) || specializedCareUrl;

  const getHref = isVardPersonal1177
    ? primaryCareUrl
      ? primaryCareUrl
      : specializedCareUrl
    : regionalizeSearchHits
    ? getRegionalizedUrl(href)
    : href;
  return (
    <TrackedLink
      HitId={HitId}
      query={query}
      DisableClickTracking={DisableClickTracking}
      TrackId={TrackId}
      className={className}
      href={regionalizeSearchHits ? getRegionalizedUrl(href) : href}
      aria-label={heading}
      containsLinks={
        availableLanguages?.[0] ||
        (vardpersonal1177 &&
          selectionCode &&
          (primaryCareUrl || specializedCareUrl))
          ? true
          : false
      }
    >
      <div className={itemContainer} data-epifind-score={score}>
        {image && (
          <div
            className="c-related__item__image"
            dangerouslySetInnerHTML={{
              __html: `<img src="${image}?preset=preset_300" />`,
            }}
          />
        )}
        {vignette && (
          <span
            className="c-related__item__vignette"
            dangerouslySetInnerHTML={{ __html: vignette }}
          />
        )}
        {(heading && isVardPersonal1177) || availableLanguages?.[0] ? (
          <A
            className="c-related__item__anchor"
            href={getHref}
            onClick={() => {
              trackLinkClick();
            }}
            aria-label={heading}
          >
            <h3
              className="c-related__item__heading"
              dangerouslySetInnerHTML={{ __html: heading }}
            />
            {availableLanguages?.[0] && text && text.length >= 1 && (
              <p
                className="c-related__item__text"
                dangerouslySetInnerHTML={{ __html: text }}
              />
            )}
          </A>
        ) : (
          <h3
            className="c-related__item__heading"
            {...(isExternalSearchHit && {
              'aria-label': `${heading}. ${translations?.leadstoexternalsite}`,
            })}
            dangerouslySetInnerHTML={{ __html: heading }}
          />
        )}
        {informationtype && informationtype.Key !== 'article' && (
          <div
            className={
              informationtype.Key
                ? 'c-related__item__information-type c-related__item__information-type--' +
                  informationtype.Key
                : 'c-related__item__information-type'
            }
          >
            {informationtype.Value}
          </div>
        )}
        {selectionCode &&
          selectionCode !== 'none' &&
          (primaryCareUrl || specializedCareUrl) && (
            <div className="c-related__item__selection-code">
              {primaryCareUrl && specializedCareUrl ? (
                <>
                  <A
                    className="c-related__item__selection-code-url"
                    href={primaryCareUrl}
                    onClick={() => {
                      trackLinkClick();
                    }}
                  >
                    Primärvård
                  </A>
                  <A
                    className="c-related__item__selection-code-url"
                    href={specializedCareUrl}
                    onClick={() => {
                      trackLinkClick();
                    }}
                  >
                    Specialiserad vård
                  </A>
                </>
              ) : (
                <A href={primaryCareUrl ? primaryCareUrl : specializedCareUrl}>
                  {primaryCareUrl ? 'Primärvård' : 'Specialiserad vård'}
                </A>
              )}
            </div>
          )}
        {text && text.length >= 1 && !availableLanguages?.[0] && (
          <p
            className="c-related__item__text"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        )}
        {availableLanguages?.[0] && (
          <div className="c-related__item__available-languages">
            {availableLanguagesHeader && (
              <span id="languages-list">{availableLanguagesHeader}</span>
            )}
            <ul
              className="c-related__item__languages"
              aria-live="polite"
              aria-labelledby="languages-list"
            >
              {availableLanguages.map((language, i) => (
                <li key={i}>
                  <A
                    href={language.Url}
                    onClick={() => trackLinkClick()}
                    className="c-related__item__language-link"
                    lang={language.Name}
                    data-event-category={href || 'novalue'}
                    data-event-action="Språklänk vald från sökresultat"
                    data-event-name={
                      language.Name + ' (' + language.NativeName + ')'
                    }
                  >
                    {language.NativeName}
                  </A>
                </li>
              ))}
            </ul>
          </div>
        )}
        {showRevised && (
          <RevisedAndPublished
            revisedAsPublished={revisedAsPublished}
            isNew={isNew}
            translations={translations}
            eventDate={eventDate}
            subjectHeading={subjectHeading}
          />
        )}

        <span className="c-related__item__link"></span>
      </div>
    </TrackedLink>
  );
};

ListItem.propTypes = {
  className: string,
  href: string,
  searchHitHeading: string,
  image: string,
  vignette: string,
  heading: string,
  text: string,
  score: oneOfType([string, number]),
  revisedAsPublished: bool,
  isNew: bool,
  eventDate: string,
  subjectHeading: string,
  translations: shape({
    subject: string,
    published: string,
    revised: string,
  }),
  showRevised: bool,
  HitId: string,
  query: string,
  TrackId: string,
  DisableClickTracking: bool,
  informationtype: PropTypes.object,
  vardpersonal1177: bool,
  availableLanguagesHeader: string,
  availableLanguages: array,
};

export default ListItem;
